import { useEffect, useState } from "react";
import { hasRole } from "@/lib/roles";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Users, Building2, CalendarCheck, Wallet, ArrowUpRight, Cake, PartyPopper, Plus, Send, Receipt, Award, Settings as SettingsIcon, MailCheck, Database } from "lucide-react";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import HrAnalytics from "@/components/HrAnalytics";
import TptPanel from "@/components/TptPanel";
import WelcomeModal from "@/components/WelcomeModal";
import DeptCostTrend from "@/components/DeptCostTrend";
import MissingDeptCard from "@/components/MissingDeptCard";
import WhosInPanel from "@/components/WhosInPanel";
import CelebrationSpotlight from "@/components/CelebrationSpotlight";
import HolidayCountdown from "@/components/HolidayCountdown";
import FaceEnrollmentCard from "@/components/FaceEnrollmentCard";
import MissingEmailCard from "@/components/MissingEmailCard";
import PlanOutgrownBanner from "@/components/PlanOutgrownBanner";
import { isFinanceLead } from "@/lib/roles";

function formatCurrency(n) {
    if (!n) return "₦0";
    return "₦" + Number(n).toLocaleString("en-NG", { maximumFractionDigits: 0 });
}

function Stat({ label, value, icon: Icon, testid, accent, compact, tone = "klein", hint }) {
    const tones = {
        klein: "bg-klein-50 text-klein",
        coral: "bg-terracotta-50 text-terracotta-500",
        gold: "bg-amber-50 text-amber-600",
        green: "bg-emerald-50 text-emerald-600",
    };
    return (
        <div
            className="group bg-white border border-ink/10 p-6 min-w-0 transition-transform hover:-translate-y-0.5"
            data-testid={testid}
        >
            <div className="flex items-center justify-between">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${accent ? "bg-klein text-white" : tones[tone]}`}>
                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                {hint && <span className="text-[11px] font-semibold text-ink/45">{hint}</span>}
            </div>
            <div className="mt-5 text-sm font-medium text-ink/60">{label}</div>
            <div
                className={`mt-1 font-display font-extrabold tracking-[-0.03em] text-ink tabular-nums truncate ${compact ? "text-2xl xl:text-3xl" : "text-4xl"}`}
                title={typeof value === "string" ? value : undefined}
            >
                {value}
            </div>
        </div>
    );
}

function greeting() {
    const h = Number(new Date().toLocaleString("en-GB", { hour: "numeric", hour12: false, timeZone: "Africa/Lagos" }));
    return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

function DashboardHero({ user }) {
    const first = (user?.name || user?.email || "").split(/[\s@]/)[0];
    const today = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Africa/Lagos" });
    const actions = [
        { to: "/employees", state: { openAdd: true }, label: "Add employee", testid: "dashboard-add-employee", primary: true, icon: Plus },
        { to: "/departments", label: "Departments", testid: "dashboard-add-department", icon: Building2 },
        { to: "/leaves", label: "Leave requests", testid: "dashboard-new-leave", icon: CalendarCheck },
    ];
    return (
        <div className="relative overflow-hidden rounded-3xl bg-[#0E1630] text-white px-7 py-8 sm:px-10 sm:py-10" data-testid="dashboard-hero">
            <div className="absolute -top-20 -right-10 w-80 h-80 rounded-full bg-klein/50 blur-3xl" />
            <div className="absolute -bottom-24 right-40 w-64 h-64 rounded-full bg-terracotta/30 blur-3xl" />
            <div className="relative flex flex-wrap items-end justify-between gap-6">
                <div>
                    <div className="text-sm text-white/60" data-testid="dashboard-date">{today}</div>
                    <h1 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-[-0.035em]" data-testid="dashboard-greeting">
                        {greeting()}, <span className="text-terracotta">{first || "there"}</span>
                    </h1>
                    <p className="mt-2 text-white/70 text-sm sm:text-base max-w-lg">Here&apos;s how your people and payroll look today.</p>
                </div>
                <div className="grid grid-cols-2 sm:flex sm:items-center gap-2 sm:flex-wrap w-full sm:w-auto" data-testid="dashboard-quick-actions">
                    {actions.map((a) => (
                        <Link key={a.testid} to={a.to} state={a.state} data-testid={a.testid}
                            className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 sm:px-4 py-2.5 text-[13px] sm:text-sm font-semibold whitespace-nowrap transition-colors ${a.primary ? "bg-[#ffffff] text-[#0F172A] hover:bg-[#ffffff]/90" : "bg-white/10 text-white hover:bg-white/20"}`}>
                            <a.icon className="w-4 h-4" /> {a.label}
                        </Link>
                    ))}
                    <Link to="/employees" data-testid="dashboard-manage-link" className="inline-flex items-center justify-center gap-1 rounded-xl border border-white/15 sm:border-0 px-3 py-2.5 sm:px-0 text-[13px] sm:text-sm text-white/80 hover:text-white sm:ml-1">
                        Manage all <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default function Dashboard() {
    const { user } = useAuth();
    if (user?.role === "employee") return (<><WelcomeModal /><EmployeeDashboard /></>);
    return (<><WelcomeModal /><AdminDashboard /></>);
}

function AdminDashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [sendingCelebrations, setSendingCelebrations] = useState(false);
    const [testEmailOpen, setTestEmailOpen] = useState(false);
    const [testEmailTo, setTestEmailTo] = useState("");
    const [sendingTest, setSendingTest] = useState(false);
    const [sendingBackup, setSendingBackup] = useState(false);
    const navigate = useNavigate();

    const onTriggerCelebrations = async () => {
        if (!window.confirm("Send today's birthday & work-anniversary emails now? This will email every employee whose birthday or work anniversary falls on today's date (Africa/Lagos).")) return;
        setSendingCelebrations(true);
        try {
            const { data } = await api.post("/notifications/celebrations/run");
            const parts = [];
            parts.push(`${data.birthdays_sent || 0} birthday email${data.birthdays_sent === 1 ? "" : "s"}`);
            parts.push(`${data.anniversaries_sent || 0} anniversary email${data.anniversaries_sent === 1 ? "" : "s"}`);
            toast.success(`Sent: ${parts.join(", ")}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not run scheduler");
        } finally {
            setSendingCelebrations(false);
        }
    };

    const onTestEmail = async () => {
        if (!testEmailTo || !testEmailTo.includes("@")) {
            toast.error("Enter a valid recipient email");
            return;
        }
        setSendingTest(true);
        try {
            const { data } = await api.post("/notifications/test-email", { to: testEmailTo });
            toast.success(`Resend accepted email · id ${data.resend_id?.slice(0, 12)}…`);
            setTestEmailOpen(false);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Test email failed");
        } finally {
            setSendingTest(false);
        }
    };

    const onTriggerBackup = async () => {
        if (!window.confirm("Generate a full Excel snapshot of the HR database and email it to all admins now? Weekly backups run automatically every Sunday at 02:00 (Africa/Lagos).")) return;
        setSendingBackup(true);
        try {
            const { data } = await api.post("/notifications/backup/run");
            const sizeKb = Math.round((data.size_bytes || 0) / 1024);
            toast.success(`Backup emailed · ${data.rows} rows · ${sizeKb} KB · to ${data.recipients?.length || 0} admin${data.recipients?.length === 1 ? "" : "s"}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Backup failed");
        } finally {
            setSendingBackup(false);
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const [statsRes, csRes] = await Promise.all([
                    api.get("/dashboard/stats"),
                    api.get("/company-settings").catch(() => ({ data: null })),
                ]);
                setStats(statsRes.data);
                // First-time admin → nudge to onboarding
                if (csRes.data && csRes.data.is_configured === false && hasRole(user, "admin")) {
                    if (!sessionStorage.getItem("onboarding-dismissed")) {
                        navigate("/settings?onboarding=1", { replace: true });
                    }
                }
            } catch (e) {
                setError("Failed to load stats");
            } finally {
                setLoading(false);
            }
        })();
    /* eslint-disable-next-line */
    }, []);

    return (
        <div className="px-5 sm:px-8 md:px-10 py-8 md:py-10" data-testid="dashboard-page">
            <DashboardHero user={user} />

            {hasRole(user, "admin", "hr") && <MissingDeptCard />}

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
                <Stat
                    label="Total employees"
                    value={loading ? "—" : stats?.total_employees ?? 0}
                    icon={Users}
                    testid="stat-total-employees"
                    accent
                />
                <Stat
                    label="Departments"
                    value={loading ? "—" : stats?.total_departments ?? 0}
                    icon={Building2}
                    testid="stat-total-departments"
                    tone="green"
                />
                <Stat
                    label="Pending leaves"
                    value={loading ? "—" : stats?.pending_leaves ?? 0}
                    icon={CalendarCheck}
                    testid="stat-pending-leaves"
                    tone="coral"
                    hint={stats?.pending_leaves ? "Needs review" : ""}
                />
                <Stat
                    label="Monthly payroll"
                    value={loading ? "—" : formatCurrency(stats?.monthly_salary)}
                    icon={Wallet}
                    testid="stat-monthly-salary"
                    tone="gold"
                    compact
                />
            </div>

            <HolidayCountdown className="mt-8" />

            {hasRole(user, "admin") && <PlanOutgrownBanner />}
            {hasRole(user, "admin", "hr") && <FaceEnrollmentCard />}
            {hasRole(user, "admin", "hr") && <MissingEmailCard />}
            {hasRole(user, "admin", "hr", "department_lead") && (
                <div className="mt-8 grid grid-cols-1 xl:grid-cols-5 gap-6">
                    <div className="xl:col-span-3"><WhosInPanel /></div>
                    <div className="xl:col-span-2"><CelebrationSpotlight /></div>
                </div>
            )}

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
                {/* Department breakdown */}
                <div className="lg:col-span-2 bg-white p-10" data-testid="dept-breakdown">
                    <div className="flex items-end justify-between mb-8">
                        <div>
                            <div className="overline">Composition</div>
                            <h2 className="mt-2 font-display font-bold text-2xl sm:text-3xl tracking-tight text-ink">
                                Headcount by department
                            </h2>
                        </div>
                        <Link
                            to="/departments"
                            className="text-sm text-ink/60 hover:text-klein hover:underline"
                            data-testid="dept-manage-link"
                        >
                            Manage →
                        </Link>
                    </div>

                    {loading ? (
                        <div className="text-sm text-ink/40">Loading…</div>
                    ) : stats?.department_breakdown?.length ? (
                        <div className="space-y-4">
                            {stats.department_breakdown.map((d) => {
                                const max = Math.max(...stats.department_breakdown.map((x) => x.count));
                                const pct = max ? (d.count / max) * 100 : 0;
                                return (
                                    <div key={d.department || "__unassigned__"} className="grid grid-cols-12 items-center gap-4 py-2 border-b border-ink/5 last:border-b-0">
                                        <div className="col-span-4 text-sm text-ink truncate">
                                            {d.department || "Unassigned"}
                                        </div>
                                        <div className="col-span-7">
                                            <div className="h-1.5 bg-ink/5">
                                                <div
                                                    className="h-full bg-klein transition-all"
                                                    style={{ width: `${pct}%` }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-span-1 text-right font-display font-bold text-ink">
                                            {d.count}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-sm text-ink/50">
                            No employees yet. Add your first employee to see breakdowns.
                        </div>
                    )}
                </div>

                {/* Recent hires */}
                <div className="bg-white p-10" data-testid="recent-hires">
                    <div className="overline">Recent</div>
                    <h2 className="mt-2 font-display font-bold text-2xl tracking-tight text-ink">
                        New hires
                    </h2>

                    <div className="mt-6 space-y-0">
                        {(stats?.recent_hires ?? []).map((e) => (
                            <Link
                                key={e.id}
                                to={`/employees/${e.id}`}
                                className="flex items-center gap-4 py-4 border-b border-ink/10 last:border-b-0 group"
                                data-testid={`recent-hire-${e.id}`}
                            >
                                <div className="w-10 h-10 bg-ink/10 overflow-hidden shrink-0">
                                    {e.photo ? (
                                        <img src={e.photo} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center font-display font-bold text-ink/60 text-sm">
                                            {(e.first_name?.[0] || "") + (e.last_name?.[0] || "")}
                                        </div>
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-ink truncate group-hover:text-klein">
                                        {e.first_name} {e.last_name}
                                    </div>
                                    <div className="text-xs text-ink/50 truncate">{e.position}</div>
                                </div>
                            </Link>
                        ))}
                        {!loading && (stats?.recent_hires ?? []).length === 0 && (
                            <div className="text-sm text-ink/50">No employees yet.</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Staff strength by department */}
            <div className="mt-16 bg-white border border-ink/10 p-10" data-testid="department-strength">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                    <div>
                        <div className="overline">Staff strength</div>
                        <h2 className="mt-2 font-display font-bold text-2xl sm:text-3xl tracking-tight text-ink">
                            Active employees by department
                        </h2>
                        <p className="mt-2 text-sm text-ink/50">
                            Headcount and monthly payroll across {(stats?.department_strength?.length || 0)} departments.
                        </p>
                    </div>
                    {!loading && stats?.department_strength && (
                        <div className="text-xs text-ink/50">
                            Total active · {stats.active_employees} staff
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="text-sm text-ink/40">Loading…</div>
                ) : !(stats?.department_strength?.length) ? (
                    <div className="text-sm text-ink/50 py-6 border-t border-ink/5">
                        No department assignments yet. Set a Department on each employee to see this breakdown.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-sm">
                            <thead>
                                <tr className="border-b-2 border-ink/20">
                                    <th className="text-left overline px-2 py-3">Department</th>
                                    <th className="text-left overline px-2 py-3">Headcount</th>
                                    <th className="text-left overline px-2 py-3 hidden md:table-cell">Share</th>
                                    <th className="text-right overline px-2 py-3">Monthly payroll</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    const max = Math.max(...stats.department_strength.map((x) => x.count));
                                    return stats.department_strength.map((c) => {
                                        const pct = max ? (c.count / max) * 100 : 0;
                                        const cKey = c.department || "__unassigned__";
                                        return (
                                            <tr key={cKey} className="border-b border-ink/10" data-testid={`dept-row-${cKey}`}>
                                                <td className="px-2 py-4 font-medium text-ink">{c.department || "Unassigned"}</td>
                                                <td className="px-2 py-4">
                                                    <span className="font-display font-bold text-ink text-lg tabular-nums">{c.count}</span>
                                                </td>
                                                <td className="px-2 py-4 hidden md:table-cell">
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-1.5 w-32 bg-ink/5">
                                                            <div className="h-full bg-klein" style={{ width: `${pct}%` }} />
                                                        </div>
                                                        <span className="text-xs text-ink/50 tabular-nums">
                                                            {((c.count / Math.max(stats.active_employees, 1)) * 100).toFixed(0)}%
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-2 py-4 text-right text-ink tabular-nums">
                                                    {formatCurrency(c.monthly_salary)}
                                                </td>
                                            </tr>
                                        );
                                    });
                                })()}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {error && <div className="mt-10 text-sm text-destructive">{error}</div>}

            {(hasRole(user, "admin", "hr", "accountant") || isFinanceLead(user)) && (
                <div className="mt-16"><DeptCostTrend /></div>
            )}

            {/* HR analytics widget — admin/hr/disbursement + dept_lead (dept-scoped) */}
            {hasRole(user, "admin", "hr", "disbursement", "department_lead") && (
                <div className="mt-16">
                    <HrAnalytics />
                </div>
            )}

            {/* Total Performance Tracker — admin/hr (company-wide) or dept-lead (own dept) */}
            {hasRole(user, "admin", "hr", "department_lead") && (
                <div className="mt-16" data-testid="tpt-dashboard-section">
                    <TptPanel
                        department={user?.role === "department_lead" ? user?.department : ""}
                        title={user?.role === "department_lead" ? "Department TPT" : "Total Performance Tracker"}
                    />
                </div>
            )}

            {/* Top performers */}
            {((stats?.top_performers || []).length > 0 || (stats?.performance_pending || 0) > 0) && (
                <div className="mt-16" data-testid="performers-widget">
                    <div className="flex items-end justify-between flex-wrap gap-4">
                        <div>
                            <div className="overline">Talent</div>
                            <h2 className="mt-2 font-display font-bold text-2xl sm:text-3xl tracking-tight text-ink">
                                Top performers
                            </h2>
                            <p className="mt-1 text-xs text-ink/50">
                                {stats?.performance_pending || 0} review{(stats?.performance_pending || 0) === 1 ? "" : "s"} awaiting approval
                            </p>
                        </div>
                        <Link to="/performance" className="inline-flex items-center gap-2 border border-klein text-klein px-4 py-2.5 text-sm hover:bg-klein hover:text-white transition-colors" data-testid="performers-link">
                            <Award className="w-4 h-4" /> All reviews
                        </Link>
                    </div>
                    {(stats?.top_performers || []).length > 0 && (
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-ink/10 border border-ink/10">
                            {(stats?.top_performers || []).map((p, i) => (
                                <div key={p.id} className="bg-white p-5" data-testid={`top-performer-${p.id}`}>
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="overline">#{i + 1}</div>
                                        <Award className={`w-4 h-4 ${i === 0 ? "text-yellow-600" : "text-ink/30"}`} />
                                    </div>
                                    <Link to={`/employees/${p.employee_id}`} className="text-ink font-medium hover:text-klein truncate block">
                                        {p.employee_name}
                                    </Link>
                                    <div className="text-xs text-ink/50 truncate">{p.department || "—"} · {p.period}</div>
                                    <div className="mt-3 flex items-baseline gap-1">
                                        <span className="font-display font-black text-3xl tabular-nums text-klein">{Number(p.normalized_score).toFixed(1)}</span>
                                        <span className="text-xs text-ink/40">/100</span>
                                    </div>
                                    <div className="text-[11px] text-ink/50 uppercase tracking-[0.15em] mt-1">{p.rating}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* F&F Settlements */}
            {(stats?.settlements_active > 0 || (stats?.settlements_recent || []).length > 0) && (
                <div className="mt-16" data-testid="settlements-widget">
                    <div className="flex items-end justify-between flex-wrap gap-4">
                        <div>
                            <div className="overline">Exit benefits</div>
                            <h2 className="mt-2 font-display font-bold text-2xl sm:text-3xl tracking-tight text-ink">
                                Full &amp; Final settlements
                            </h2>
                            <p className="mt-1 text-xs text-ink/50">
                                {stats?.settlements_active || 0} active · awaiting Department, Finance, or Disbursement approval
                            </p>
                        </div>
                        <Link
                            to="/settlements"
                            className="inline-flex items-center gap-2 border border-klein text-klein px-4 py-2.5 text-sm hover:bg-klein hover:text-white transition-colors"
                            data-testid="settlements-widget-link"
                        >
                            <Receipt className="w-4 h-4" /> Open settlements
                        </Link>
                    </div>
                    <div className="mt-4 bg-white border border-ink/10 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b-2 border-ink/20 bg-bone">
                                    <th className="text-left overline px-4 py-3">Employee</th>
                                    <th className="text-left overline px-4 py-3 hidden md:table-cell">Type</th>
                                    <th className="text-left overline px-4 py-3 hidden md:table-cell">Effective</th>
                                    <th className="text-right overline px-4 py-3">Net</th>
                                    <th className="text-left overline px-4 py-3">Stage</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(stats?.settlements_recent || []).slice(0, 5).map((s) => (
                                    <tr key={s.id} className="border-b border-ink/10" data-testid={`dashboard-settlement-${s.id}`}>
                                        <td className="px-4 py-3">
                                            <Link to="/settlements" className="text-ink hover:text-klein font-medium">{s.employee_name}</Link>
                                            <div className="text-xs text-ink/50 md:hidden capitalize">{s.kind} · {s.end_date}</div>
                                        </td>
                                        <td className="px-4 py-3 capitalize hidden md:table-cell">{s.kind}</td>
                                        <td className="px-4 py-3 hidden md:table-cell">{s.end_date || "—"}</td>
                                        <td className="px-4 py-3 text-right tabular-nums font-display font-bold text-klein">
                                            {formatCurrency(s.net_payable)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="inline-flex items-center px-2 py-1 border text-[10px] uppercase tracking-[0.15em] bg-ink/5 border-ink/10 text-ink/70">
                                                {(s.status || "").replace(/_/g, " ").replace("pending ", "→ ")}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Rent receipts expiring — protect PAYE relief on next run */}
            {((stats?.rent_receipts_expiring_soon || []).length > 0) && (
                <div className="mt-16" data-testid="rent-expiry-widget">
                    <div className="flex items-end justify-between flex-wrap gap-4">
                        <div>
                            <div className="overline">PAYE rent relief at risk</div>
                            <h2 className="mt-2 font-display font-bold text-2xl sm:text-3xl tracking-tight text-ink">
                                Rent receipts expiring soon
                            </h2>
                            <p className="mt-1 text-xs text-ink/50">
                                {stats.rent_receipts_expiring_count} employee{stats.rent_receipts_expiring_count === 1 ? "" : "s"} need a fresh receipt within 30 days — otherwise their next payroll loses the rent relief.
                            </p>
                        </div>
                        <Link
                            to="/pending-approvals"
                            className="inline-flex items-center gap-2 border border-amber-500 text-amber-700 px-4 py-2.5 text-sm hover:bg-amber-50 transition-colors"
                            data-testid="rent-expiry-widget-link"
                        >
                            <Receipt className="w-4 h-4" /> Manage rent receipts
                        </Link>
                    </div>
                    <div className="mt-4 bg-white border border-ink/10 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b-2 border-ink/20 bg-bone">
                                    <th className="text-left overline px-4 py-3">Employee</th>
                                    <th className="text-left overline px-4 py-3 hidden md:table-cell">Department</th>
                                    <th className="text-right overline px-4 py-3 hidden md:table-cell">Annual rent</th>
                                    <th className="text-left overline px-4 py-3">Expires</th>
                                    <th className="text-left overline px-4 py-3">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(stats.rent_receipts_expiring_soon || []).slice(0, 8).map((r) => {
                                    const pill = r.expired
                                        ? "bg-red-50 text-red-700 border-red-200"
                                        : r.days_left <= 7
                                            ? "bg-amber-50 text-amber-800 border-amber-200"
                                            : "bg-yellow-50 text-yellow-800 border-yellow-200";
                                    const label = r.expired
                                        ? `Expired ${Math.abs(r.days_left)}d ago`
                                        : `${r.days_left}d left`;
                                    return (
                                        <tr key={r.employee_id} className="border-b border-ink/10 hover:bg-bone/40" data-testid={`rent-expiry-row-${r.employee_id}`}>
                                            <td className="px-4 py-3">
                                                <Link to={`/employees/${r.employee_id}`} className="text-ink hover:text-klein font-medium">
                                                    {r.name}
                                                </Link>
                                                <div className="text-xs text-ink/50">{r.employee_no || "—"}</div>
                                            </td>
                                            <td className="px-4 py-3 text-ink/70 hidden md:table-cell">{r.department || "—"}</td>
                                            <td className="px-4 py-3 text-right tabular-nums hidden md:table-cell">{formatCurrency(r.annual_rent)}</td>
                                            <td className="px-4 py-3 text-ink/80 tabular-nums">{r.expiry}</td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center px-2 py-1 border text-[10px] uppercase tracking-[0.15em] ${pill}`}>
                                                    {label}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Birthdays & Anniversaries */}
            <div className="mt-16 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="overline">Celebrations</div>
                    <h2 className="mt-2 font-display font-bold text-2xl sm:text-3xl tracking-tight text-ink">
                        Birthdays & anniversaries
                    </h2>
                    <p className="mt-1 text-xs text-ink/50">
                        Auto-mailed daily at 08:00 (Africa/Lagos). Use &quot;Send today&apos;s emails&quot; to trigger manually. Full database backup auto-emails to admins every Sunday at 02:00.
                    </p>
                </div>
                {hasRole(user, "admin") && (
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            onClick={onTriggerBackup}
                            disabled={sendingBackup}
                            data-testid="trigger-backup-button"
                            className="inline-flex items-center gap-2 border border-ink/20 text-ink/70 px-4 py-2.5 text-sm hover:bg-ink hover:text-white transition-colors disabled:opacity-60"
                            title="Email a full Excel database snapshot to all admins now (also runs automatically every Sunday at 02:00 WAT)"
                        >
                            <Database className="w-4 h-4" />
                            {sendingBackup ? "Sending…" : "Backup now"}
                        </button>
                        <button
                            onClick={() => { setTestEmailTo(user?.email || ""); setTestEmailOpen(true); }}
                            data-testid="test-email-button"
                            className="inline-flex items-center gap-2 border border-ink/20 text-ink/70 px-4 py-2.5 text-sm hover:bg-ink hover:text-white transition-colors"
                            title="Send a one-off test email to verify Resend delivery"
                        >
                            <MailCheck className="w-4 h-4" />
                            Test email
                        </button>
                        <button
                            onClick={onTriggerCelebrations}
                            disabled={sendingCelebrations}
                            data-testid="trigger-celebrations-button"
                            className="inline-flex items-center gap-2 border border-klein text-klein px-4 py-2.5 text-sm hover:bg-klein hover:text-white transition-colors disabled:opacity-60"
                            title="Run the daily birthday + work-anniversary email job now"
                        >
                            <Send className="w-4 h-4" />
                            {sendingCelebrations ? "Sending…" : "Send today's emails"}
                        </button>
                    </div>
                )}
            </div>
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-px bg-ink/10 border border-ink/10" data-testid="celebrations-widget">
                <CelebrationList
                    title="Birthdays"
                    subtitle={stats?.current_month_name ? `in ${stats.current_month_name}` : ""}
                    icon={Cake}
                    items={stats?.birthdays || []}
                    dateKey="date_of_birth"
                    loading={loading}
                    emptyText="No birthdays this month. Enjoy the calm."
                    testid="birthdays-list"
                    formatMeta={(e) => `${monthDay(e.date_of_birth)} · ${e.position || "—"}`}
                />
                <CelebrationList
                    title="Work anniversaries"
                    subtitle={stats?.current_month_name ? `in ${stats.current_month_name}` : ""}
                    icon={PartyPopper}
                    items={stats?.anniversaries || []}
                    dateKey="joining_date"
                    loading={loading}
                    emptyText="No work anniversaries this month."
                    testid="anniversaries-list"
                    formatMeta={(e) => `${e.years} ${e.years === 1 ? "year" : "years"} · joined ${monthDay(e.joining_date)}`}
                    accent
                />
            </div>

            {testEmailOpen && (
                <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-start justify-center pt-24 px-4" onClick={() => setTestEmailOpen(false)} data-testid="test-email-dialog">
                    <div onClick={(e) => e.stopPropagation()} className="bg-white border border-ink/10 w-full max-w-md">
                        <div className="px-6 py-5 border-b border-ink/10">
                            <div className="overline">Diagnostics</div>
                            <h3 className="mt-1 font-display font-bold text-lg">Send a test email</h3>
                            <p className="text-xs text-ink/50 mt-2">
                                Confirms whether Resend can actually deliver from your sender address. If your domain isn&apos;t yet verified, the call may succeed but the email won&apos;t arrive — check your Resend dashboard for the resulting ID.
                            </p>
                        </div>
                        <div className="px-6 py-5">
                            <label className="overline block mb-2">Recipient</label>
                            <input
                                type="email"
                                value={testEmailTo}
                                onChange={(e) => setTestEmailTo(e.target.value)}
                                placeholder="you@example.com"
                                className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                data-testid="test-email-to"
                                autoFocus
                            />
                        </div>
                        <div className="px-6 py-4 border-t border-ink/10 flex items-center justify-end gap-3">
                            <button onClick={() => setTestEmailOpen(false)} className="px-5 py-2.5 border border-ink/20 text-sm hover:bg-ink/5">Cancel</button>
                            <button onClick={onTestEmail} disabled={sendingTest} className="inline-flex items-center gap-2 px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="test-email-send">
                                <MailCheck className="w-4 h-4" /> {sendingTest ? "Sending…" : "Send test"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function monthDay(iso) {
    if (!iso || iso.length < 10) return "";
    const d = new Date(iso + "T00:00:00");
    if (isNaN(d)) return iso;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function CelebrationList({ title, subtitle, icon: Icon, items, loading, emptyText, testid, formatMeta, accent }) {
    return (
        <div className="bg-white p-10" data-testid={testid}>
            <div className="flex items-start justify-between mb-6">
                <div>
                    <div className="overline">{subtitle}</div>
                    <h2 className="mt-2 font-display font-bold text-2xl tracking-tight text-ink flex items-center gap-3">
                        <Icon className="w-5 h-5 text-klein" strokeWidth={1.5} />
                        {title}
                    </h2>
                </div>
                {items.length > 0 && (
                    <div className={`text-xs uppercase tracking-[0.15em] border px-2 py-1 ${accent ? "border-klein text-klein" : "border-ink/20 text-ink/60"}`}>
                        {items.length}
                    </div>
                )}
            </div>
            {loading ? (
                <div className="text-sm text-ink/40">Loading…</div>
            ) : items.length === 0 ? (
                <div className="text-sm text-ink/50 py-6 border-t border-ink/5">{emptyText}</div>
            ) : (
                <div>
                    {items.map((e) => (
                        <Link
                            to={`/employees/${e.id}`}
                            key={e.id}
                            className="flex items-center gap-4 py-4 border-t border-ink/10 group"
                            data-testid={`${testid}-item-${e.id}`}
                        >
                            <div className="w-11 h-11 bg-ink/10 overflow-hidden shrink-0">
                                {e.photo ? (
                                    <img src={e.photo} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center font-display font-bold text-ink/60 text-sm">
                                        {(e.first_name?.[0] || "") + (e.last_name?.[0] || "")}
                                    </div>
                                )}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-ink group-hover:text-klein truncate">
                                    {e.first_name} {e.last_name}
                                </div>
                                <div className="text-xs text-ink/50 truncate">{formatMeta(e)}</div>
                            </div>
                            <div className="font-display font-black text-ink text-lg tabular-nums">
                                {String(e.day).padStart(2, "0")}
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
